import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "@shadcn/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@shadcn/components/ui/table";
import { Button } from "@shadcn/components/ui/button";
import { Download, ChevronDown, ChevronRight } from 'lucide-react';
import { Pie } from 'react-chartjs-2';
import { format } from "date-fns";
import * as XLSX from 'xlsx';

const ByWarehouseLocationTab = ({
  data,
  hierarchicalData,
  pieData,
  loading,
  filters,
  groupBy
}) => {
  
  const [expandedWarehouses, setExpandedWarehouses] = useState({});

  const toggleWarehouse = (warehouse) => {
    setExpandedWarehouses(prev => ({
      ...prev,
      [warehouse]: !prev[warehouse]
    }));
  };
  const exportToExcel = () => {
    if (!hierarchicalData?.hierarchicalData || !hierarchicalData?.warehouses) return;

    try {
      
      const workbook = XLSX.utils.book_new();

      
      const mainData = [];

      
      const headers = ['Warehouse Location', 'Category', 'Product', 'SKU', 'Quantity', 'Percentage'];
      mainData.push(headers);

      const { hierarchicalData: hData, warehouses, total } = hierarchicalData;

      
      warehouses.forEach(warehouse => {
        Object.entries(hData[warehouse] || {}).forEach(([category, categoryData]) => {
          Object.entries(categoryData.products || {}).forEach(([product, productData]) => {
            Object.entries(productData.skus || {}).forEach(([sku, skuData]) => {
              mainData.push([
                warehouse,
                category,
                product,
                sku,
                skuData.quantity,
                total > 0 ? ((skuData.quantity / total) * 100).toFixed(1) + '%' : '0%'
              ]);
            });
          });
        });
      });

      
      const totalRow = ['GRAND TOTAL'];
      for (let i = 1; i < headers.length - 2; i++) {
        totalRow.push('');
      }
      totalRow.push(total || 0, '100%');
      mainData.push(totalRow);

      
      const worksheet = XLSX.utils.aoa_to_sheet(mainData);

      
      const columnWidths = [
        { wch: 20 },
        { wch: 20 },
        { wch: 25 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 } 
      ];
      worksheet['!cols'] = columnWidths;

      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Warehouse Location Data');

      
      const summaryData = [];

      
      summaryData.push(['Warehouse Location Report Summary']);
      summaryData.push(['']);

      
      if (filters?.startDate && filters?.endDate) {
        summaryData.push(['Report Period:', `${format(filters.startDate, "dd/MM/yyyy")} to ${format(filters.endDate, "dd/MM/yyyy")}`]);
      }

      
      summaryData.push(['']);
      summaryData.push(['Report Details:']);
      summaryData.push(['Group By:', groupBy || 'category']);
      summaryData.push(['Total Warehouses:', warehouses?.length || 0]);
      summaryData.push(['Grand Total Quantity:', total || 0]);

      summaryData.push(['']);
      summaryData.push(['Warehouse Summary:']);
      warehouses.forEach(warehouse => {
        const warehouseTotal = Object.values(hData[warehouse] || {}).reduce((sum, cat) => sum + cat.total, 0);
        summaryData.push([
          warehouse,
          warehouseTotal,
          total > 0 ? ((warehouseTotal / total) * 100).toFixed(1) + '%' : '0%'
        ]);
      });

      
      const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);

      
      summaryWorksheet['!cols'] = [
        { wch: 25 },
        { wch: 20 },
        { wch: 15 }
      ];


      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');


      const dateStr = format(new Date(), 'yyyy-MM-dd');
      const filename = `Warehouse_Location_Report_${dateStr}.xlsx`;


      XLSX.writeFile(workbook, filename);

      return true;
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
      return false;
    }
  };

  const renderHierarchicalTable = () => {
    if (!hierarchicalData?.hierarchicalData || !hierarchicalData?.warehouses) {
      return (
        <div className="flex justify-center items-center h-32">
          <div className="text-gray-500">No hierarchical data available</div>
        </div>
      );
    }

    const { hierarchicalData: hData, warehouses } = hierarchicalData;

    
    const showSKU = groupBy === "sku";
    const showProduct = groupBy === "sku" || groupBy === "product";
    const showCategory = true; 

    return (
      <div className="overflow-x-auto">
        <Table className="w-full">
          <TableHeader>
            <TableRow>
              <TableHead>Warehouse Location</TableHead>
              {showCategory && <TableHead>Category</TableHead>}
              {showProduct && <TableHead>Product</TableHead>}
              {showSKU && <TableHead>SKU</TableHead>}
              <TableHead className="text-right">Quantity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {warehouses.map(warehouse => {
              const isWarehouseExpanded = expandedWarehouses[warehouse];
              const warehouseTotal = Object.values(hData[warehouse] || {}).reduce((sum, cat) => sum + cat.total, 0);

              return (
                <React.Fragment key={warehouse}>
                  {/* Warehouse Header */}
                  <TableRow
                    className="bg-blue-100 font-bold cursor-pointer hover:bg-blue-200"
                    onClick={() => toggleWarehouse(warehouse)}
                  >
                    <TableCell className="flex items-center">
                      {isWarehouseExpanded ? <ChevronDown className="h-4 w-4 mr-2" /> : <ChevronRight className="h-4 w-4 mr-2" />}
                      {warehouse}
                    </TableCell>
                    {showCategory && <TableCell></TableCell>}
                    {showProduct && <TableCell></TableCell>}
                    {showSKU && <TableCell></TableCell>}
                    <TableCell className="text-right font-bold">{warehouseTotal}</TableCell>
                  </TableRow>

                  
                  {isWarehouseExpanded && Object.entries(hData[warehouse] || {}).map(([category, categoryData]) => {

                    if (groupBy === "category") {
                  
                      return (
                        <TableRow key={`${warehouse}-${category}`} className="hover:bg-gray-50">
                          <TableCell className="pl-8"></TableCell>
                          <TableCell>{category}</TableCell>
                          <TableCell className="text-right">{categoryData.total}</TableCell>
                        </TableRow>
                      );
                    } else if (groupBy === "product") {
                  
                      return Object.entries(categoryData.products || {}).map(([product, productData]) => (
                        <TableRow key={`${warehouse}-${category}-${product}`} className="hover:bg-gray-50">
                          <TableCell className="pl-8"></TableCell>
                          <TableCell>{category}</TableCell>
                          <TableCell>{product}</TableCell>
                          <TableCell className="text-right">{productData.total}</TableCell>
                        </TableRow>
                      ));
                    } else if (groupBy === "sku") {
                      // Show category -> product -> sku level
                      return Object.entries(categoryData.products || {}).map(([product, productData]) =>
                        Object.entries(productData.skus || {}).map(([sku, skuData]) => (
                          <TableRow key={`${warehouse}-${category}-${product}-${sku}`} className="hover:bg-gray-50">
                            <TableCell className="pl-8"></TableCell>
                            <TableCell>{category}</TableCell>
                            <TableCell>{product}</TableCell>
                            <TableCell>{sku}</TableCell>
                            <TableCell className="text-right">{skuData.quantity}</TableCell>
                          </TableRow>
                        ))
                      ).flat();
                    }

                    return null;
                  }).flat().filter(Boolean)}
                </React.Fragment>
              );
            })}

            {/* Grand Total Row */}
            <TableRow className="bg-yellow-100 font-bold border-t-2">
              <TableCell className="font-bold">TOTAL</TableCell>
              {showCategory && <TableCell></TableCell>}
              {showProduct && <TableCell></TableCell>}
              {showSKU && <TableCell></TableCell>}
              <TableCell className="text-right font-bold">{hierarchicalData.total}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-32">
          <div className="text-gray-500">Loading...</div>
        </div>
      );
    }

    if (!data?.tableData || data.tableData.length === 0) {
      return (
        <div className="flex justify-center items-center h-32">
          <div className="text-gray-500">No data available</div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {pieData && (
          <div>
            <h3 className="text-lg font-medium mb-2">Warehouse Location Distribution</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="h-[400px] flex items-center justify-center">
                <Pie 
                  data={pieData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          usePointStyle: true,
                          padding: 20
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const value = context.raw;
                            const percentage = ((value / data.total) * 100).toFixed(1);
                            return `${context.label}: ${value} (${percentage}%)`;
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
              <div>
                <Table className="w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Warehouse Location</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Percentage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.warehouseLocationSummary?.slice(0, 10).map((row, index) => (
                      <TableRow key={`warehouse-${index}`}>
                        <TableCell className="whitespace-nowrap">
                          {row?.warehouseLocation || 'Unknown'}
                        </TableCell>
                        <TableCell className="text-right">{row?.quantity || 0}</TableCell>
                        <TableCell className="text-right">
                          {data.total > 0 ? ((row?.quantity / data.total) * 100).toFixed(1) : 0}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {(data.warehouseLocationSummary?.length || 0) > 10 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-sm text-gray-500">
                          And {(data.warehouseLocationSummary?.length || 0) - 10} other warehouse locations
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow className="bg-gray-50">
                      <TableCell className="font-bold">Total</TableCell>
                      <TableCell className="text-right font-bold">{data.total || 0}</TableCell>
                      <TableCell className="text-right font-bold">100%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}

        {/* Hierarchical table */}
        <div>
          
          {renderHierarchicalTable()}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>
            Sale state by Warehouse Location
            <span className="ml-2 text-sm font-normal">
              ({filters?.startDate && filters?.endDate ? 
                `${format(filters.startDate, "dd/MM/yyyy")} to ${format(filters.endDate, "dd/MM/yyyy")}` : 
                'No date range selected'
              })
            </span>
          </CardTitle>
          <Button
            onClick={exportToExcel}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            disabled={!data?.tableData || data.tableData.length === 0}
          >
            <Download className="h-4 w-4" />
            Export Excel
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default ByWarehouseLocationTab; 
